import {ApplicationConfig, inject, LOCALE_ID, PLATFORM_ID, provideZonelessChangeDetection} from '@angular/core';
import {provideRouter, Router, withInMemoryScrolling} from '@angular/router';
import {routes} from './app.routes';
import {provideClientHydration, withNoHttpTransferCache} from '@angular/platform-browser';
import {HTTP_INTERCEPTORS, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi} from "@angular/common/http";
import {ConfirmationService, MessageService} from "primeng/api";
import {ErrorInterceptor} from "./core/interceptors/error.interceptor";
import {QueryInterceptor} from "./core/interceptors/query.interceptor";
import {ResponseInterceptor} from "./core/interceptors/response.interceptor";
import {DialogService} from "primeng/dynamicdialog";
import {isPlatformBrowser, registerLocaleData} from "@angular/common";
import localeFr from '@angular/common/locales/fr';
import {provideNgxStripe} from "ngx-stripe";
import {MSAL_GUARD_CONFIG, MSAL_INSTANCE, MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalService} from "@azure/msal-angular";
import {InteractionRequiredAuthError, InteractionType, PublicClientApplication} from "@azure/msal-browser";
import {EMPTY, from, Observable, switchMap} from "rxjs";
import {catchError} from "rxjs/operators";
import {environment} from "../environments/environment";
import {ErrorService} from "./core/services/commons/error.service";
import {LoadingService} from "./core/services/commons/loading.service";
import {providePrimeNG} from "primeng/config";
import Aura from "@primeng/themes/aura";

registerLocaleData(localeFr);

export function MSALInstanceFactory() {
  const platformId = inject(PLATFORM_ID);
  const isBrowser = isPlatformBrowser(platformId);

  if (!isBrowser) {

    return {
      initialize: () => Promise.resolve(),
      getAllAccounts: () => [],
      acquireTokenSilent: () => Promise.reject('SSR'),
      logout: () => {},
      initializeWrapperLibrary: () => {},
      addEventCallback: () => null,
      removeEventCallback: () => {},
      addPerformanceCallback: () => null,
      removePerformanceCallback: () => {},
      handleRedirectPromise: () => Promise.resolve(null),
      loginPopup: () => Promise.reject('SSR'),
      loginRedirect: () => Promise.reject('SSR'),
      logoutPopup: () => Promise.reject('SSR'),
      logoutRedirect: () => Promise.reject('SSR'),
      getActiveAccount: () => null,
      setActiveAccount: () => {},
      getAccountByHomeId: () => null,
      getAccountByLocalId: () => null,
      getAccountByUsername: () => null,
      getLogger: () => ({
        verbose: () => {},
        error: () => {},
        warning: () => {},
        info: () => {},
        trace: () => {},
        clone: () => ({
          verbose: () => {},
          error: () => {},
          warning: () => {},
          info: () => {},
          trace: () => {},
          clone: () => {}
        })
      })
    } as any;
  }

  return new PublicClientApplication({
    auth: {
      clientId: environment.azureClientId,
      authority: 'https://login.microsoftonline.com/' + environment.azureTenant,
      redirectUri: environment.clientEndpoint + '/admin',
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      allowNativeBroker: false,
      windowHashTimeout: 60000,
      iframeHashTimeout: 6000,
      loadFrameTimeout: 0,
      asyncPopups: false
    }
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['openid', 'profile', 'email', 'api://' + environment.azureClientId + '/user_impersonation']
    },
    loginFailedRoute: '/login'
  };
}

export const msalInterceptorFn: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) {
    return next(req);
  }

  if(req.url.includes('s3-dev.a-lex.tech')) {
    return next(req);
  }

  const services = {
    error: inject(ErrorService),
    message: inject(MessageService),
    loading: inject(LoadingService),
    msal: inject(MsalService),
    router: inject(Router)
  };

  if (!services.router.url.includes('/admin')) {
    return next(req);
  }

  const AUTH_SCOPE = [`api://${environment.azureClientId}/user_impersonation`];

  const handleError = (err: any) => {
    const errorMessage = err.error?.['description'] ||
      err.error?.detail ||
      err.error?.error ||
      err.message ||
      err.toString();

    services.error.serverErrors.update(() => [errorMessage]);

    if (services.router?.url.includes('/admin')) {
      services.message.add({
        severity: 'error',
        summary: 'Erreur serveur',
        detail: errorMessage
      });
    }

    services.loading.currentLoading.update(() => []);
    return EMPTY;
  };

  const addAuthHeader = (token: string) => {
    return req.clone({
      setHeaders: {Authorization: `Bearer ${token}`}
    });
  };

  if (!isPlatformBrowser(inject(PLATFORM_ID))) {
    return next(req);
  }

  return from(services.msal.instance.initialize()).pipe(
    switchMap(() => {
      const account = services.msal.instance.getAllAccounts()[0];

      if (!account) {
        return next(req);
      }

      return from(services.msal.instance.acquireTokenSilent({
        scopes: AUTH_SCOPE,
        account
      })).pipe(
        switchMap(response => {
          if (!response?.accessToken) {
            return next(req);
          }

          const authenticatedReq = addAuthHeader(response.accessToken);
          return next(authenticatedReq).pipe(
            catchError(handleError)
          );
        }),
        catchError(err => {
          if (err instanceof InteractionRequiredAuthError) {
            services.msal.instance.loginRedirect({
              scopes: AUTH_SCOPE,
              account
            });
            return EMPTY;
          }
          return handleError(err);
        })
      );
    }),
    catchError(handleError)
  );
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes, withInMemoryScrolling({scrollPositionRestoration: 'top'})),
    provideClientHydration(),
    provideHttpClient(withFetch(), withInterceptorsFromDi(), withInterceptors([msalInterceptorFn])),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.dark-mode',
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities'
          }
        }
      }
    }),
    {provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory},
    {provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory},
    provideNgxStripe(),
    MessageService,
    ConfirmationService,
    DialogService,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    ErrorService,
    {provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: QueryInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: ResponseInterceptor, multi: true},
    {provide: LOCALE_ID, useValue: 'fr'},
  ]
};
